import { forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, first, map, switchMap, tap, toArray } from 'rxjs/operators';

import { chat, model, maxContextWindow } from './gpt.js';
import { categorizeConversation } from './categorize.js';
import { buildPrompt, buildCitation } from './prompts.js';
import { nestMarkdown } from '../../backend/lib/markdown.js';
import { parallelInternetResearch, extractRelevantInformation } from './internet_research.js';
import { findSections } from './knowledge-api.js';
import { parallelPaperResearch } from './paper_research.js';

let productSearchAgentEnabled = false;

export function researchAgent(agentApi, msg) {
    return of(undefined).pipe(
        switchMap(() => agentApi.currentChatMessages().pipe(
            switchMap(messages => categorizeConversation(messages)),
            tap(cat => agentApi.appendChatMessage({
                role: 'categorization',
                content: cat,
                model,
            })),
        )),
        switchMap(cat => {
            if (productSearchAgentEnabled && cat.productSearch) {
                return agentApi.switchAgent('product-hunt', msg);
            }
            return research(agentApi, cat);
        }),
    );
}

function research(agentApi, cat) {
    return of(undefined).pipe(
        switchMap(() => {
            let searches$ = [];
            if (agentApi.collectKnowledgeEnabled()) {
                searches$.push(agentApi.currentChatMessages().pipe(
                    switchMap(messages => collectKnowledge(agentApi, messages)),
                ));
            }
            if (agentApi.internetResearchEnabled()) {
                if (cat.internetResearchSearchQueries?.length) {
                    searches$.push(agentApi.currentChatMessages().pipe(
                        switchMap(messages => parallelInternetResearch(messages, cat.internetResearchSearchQueries, cat.summarizingQuestion)),
                        map(research => ({
                            role: 'internet-research',
                            content: research,
                            model,
                        })),
                        toArray(),
                    ));
                }
                if (cat.scientificPaperSearchTerms?.length) {
                    searches$.push(agentApi.currentChatMessages().pipe(
                        switchMap(messages => parallelPaperResearch(messages, cat.scientificPaperSearchTerms, cat.summarizingQuestion)),
                        map(research => ({
                            role: 'paper-research',
                            content: research,
                            model,
                        })),
                        toArray(),
                    ));
                }
            }
            if (searches$.length === 0) {
                return of(undefined);
            }
            return forkJoin(searches$).pipe(
                tap(searches => {
                    // reverse the searches so that the information that
                    // we assume is most important comes last.
                    searches.reverse();
                    for (let results of searches) {
                        if (!results) {
                            continue;
                        }
                        results.reverse();
                        for (let res of results) {
                            if (res) {
                                agentApi.appendChatMessage(res);
                            }
                        }
                    }
                }),
            );
        }),
        switchMap(() => agentApi.currentChatMessages()),
        switchMap(messages => {
            let chatRoles = new Set(['system', 'user', 'assistant']);
            let chatMessages = messages.filter(msg => chatRoles.has(msg.role));
            let lastMessage = chatMessages[chatMessages.length - 1];
            if (lastMessage?.role === 'user') {
                // we only add additional information if the last
                // message was created by the user. this case
                // should be the norm.
                let knowledge = [];
                for (let i = messages.length - 1; i >= 0; --i) {
                    let msg = messages[i];
                    if (msg.role === 'assistant' || msg.role === 'system') {
                        break;
                    }
                    if (msg.role === 'knowledge') {
                        if (msg.content) {
                            knowledge.push(msg);
                        }
                    }
                    if (msg.role === 'internet-research' || msg.role === 'paper-research') {
                        if (msg.content.extract) {
                            knowledge.push(msg);
                        }
                    }
                }
                if (knowledge.length > 0) {
                    let knowledgePrompt = knowledge.map(k => {
                        switch (k.role) {
                        default:
                            throw new Error(`Unexpected knowledge: ${k.role}`);
                        case 'knowledge': {
                            let knowledgeUrl = `${window.location.protocol}//${window.location.host}/knowledge-browser.html`;
                            return `# Knowledge\n\nIf not otherwise stated in the subchapters this knowledge chapter contains information provided by ${buildCitation(knowledgeUrl)}.\n\n${nestMarkdown(k.content)}\n`;
                        }
                        case 'internet-research':
                            return `# Internet Research Results\n\nUse the citation ${buildCitation(k.content.href)} when citing content from this chapter.\n\n${nestMarkdown(k.content.extract)}\n`;
                        case 'paper-research':
                            return `# Scientific Paper Research Results\n\nUse the citation ${buildCitation(k.content.href)} when citing content from this chapter.\n\n${nestMarkdown(k.content.extract)}\n`;
                        }
                    }).join('\n');
                    return buildPrompt('answer-with-knowledge', {
                        user: nestMarkdown(lastMessage.content),
                        knowledge: nestMarkdown(knowledgePrompt),
                        exampleCitation: buildCitation('https://www.example.org/some/page'),
                    }).pipe(
                        map(contextMessage => {
                            chatMessages[chatMessages.length - 1] = {
                                ...lastMessage,
                                content: contextMessage,
                            };
                            let avgCharactersPerToken = 2;
                            return {
                                chatMessages,
                                contextSize: Math.min(4096 + contextMessage.length / avgCharactersPerToken, maxContextWindow),
                            };
                        }),
                    );
                }
            }
            return of({
                chatMessages,
                contextSize: 4096,
            });
        }),
        map(({ chatMessages, contextSize }) => ({
            // strip metadata not needed for LLM completion
            chatMessages: chatMessages.map(msg => ({
                role: msg.role,
                content: msg.content,
            })),
            contextSize,
        })),
        switchMap(({ chatMessages, contextSize }) => {
            return chat(chatMessages, {
                balancer_options: {
                    security_level: 100,
                },
                options: {
                    num_ctx: contextSize,
                    temperature: agentApi.zeroTemperatureEnabled() ? 0 : 1,
                },
            }).pipe(
                // the following custom operator injects
                // artificial done:true messages if the server
                // omitted them.
                messages$ => {
                    let done = false;
                    return new Observable(observer => {
                        let sub = messages$.subscribe({
                            next: msg => {
                                if (msg.done) {
                                    done = true;
                                }
                                observer.next(msg);
                            },
                            error: err => observer.error(err),
                            complete: () => {
                                if (!done) {
                                    console.warn('Server omitted done:true message', msg);
                                    observer.next({
                                        done: true,
                                        message: {
                                            content: '',
                                        },
                                    });
                                }
                                observer.complete();
                            },
                        });
                        return () => sub.unsubscribe();
                    });
                },
            );
        }),
    );
}

function collectKnowledge(agentApi, messages) {
    return of(undefined).pipe(
        switchMap(() => {
            let queries = [];
            let lastCat = findLastMessageByRole(messages, 'categorization');
            if (lastCat?.content?.topics?.length) {
                queries = [
                    ...queries,
                    ...lastCat.content.topics,
                ];
            }
            let lastUser = findLastMessageByRole(messages, 'user');
            queries.push(lastUser.content);
            if (queries.length === 0) {
                return of(null);
            }
            let searches$ = [];
            for (let query of queries) {
                searches$.push(findSections(query).pipe(
                    map(sections => sections.map(section => {
                        let text = section.text.trim();
                        let fragments = [];
                        if (text.startsWith('# ')) {
                            let lines = text.split('\n');
                            fragments.push(lines[0] + '\n\n');
                            text = lines.slice(1).join('\n').trim();
                        } else {
                            fragments.push('# Contextual Information\n\n');
                        }
                        if (section.doc_source_uri) {
                            fragments.push(`Note: If not otherwise stated this chapter can be cited as ${buildCitation(section.doc_source_uri)}\n\n`);
                        }
                        fragments.push(text);
                        return {
                            ...section,
                            text: fragments.join(''),
                        };
                    })),
                ));
            }
            return forkJoin(searches$).pipe(
                map(allSections => {
                    let visitedIds = new Set();
                    let mergedSections = [];
                    for (let sections of allSections) {
                        for (let i = 0; i < sections.length; ++i) {
                            let s = sections[i];
                            if (visitedIds.has(s.id)) {
                                continue;
                            }
                            visitedIds.add(s.id);
                            mergedSections.push(s.text);
                        }
                    }
                    return mergedSections.join('\n\n\n');
                }),
            );
        }),
        map(extract => extract && [
            {
                role: 'knowledge',
                content: extract,
                model,
            },
        ]),
        first(),
    );
}

function findLastMessageByRole(messages, role) {
    let reversedMessages = [...messages];
    reversedMessages.reverse();
    return reversedMessages.find(msg => msg.role === role);
}
